// Mylodic — shared TypeScript types

export const POST_COLORS = [
  { hex: '#ef4444', label: 'Red',    vibe: 'anger, passion, intensity' },
  { hex: '#3b82f6', label: 'Blue',   vibe: 'sadness, calm, loneliness' },
  { hex: '#eab308', label: 'Yellow', vibe: 'happiness, energy, optimism' },
  { hex: '#22c55e', label: 'Green',  vibe: 'growth, jealousy, balance' },
  { hex: '#a855f7', label: 'Purple', vibe: 'creativity, mystery, luxury' },
  { hex: '#f97316', label: 'Orange', vibe: 'excitement, warmth, enthusiasm' },
  { hex: '#1a1a1a', label: 'Black',  vibe: 'grief, fear, power' },
  { hex: '#e2e8f0', label: 'White',  vibe: 'purity, peace, emptiness' },
  { hex: '#ec4899', label: 'Pink',   vibe: 'love, softness, compassion' },
  { hex: '#6b7280', label: 'Gray',   vibe: 'neutrality, boredom, detachment' },
] as const;

export type PostColorHex = typeof POST_COLORS[number]['hex'];

export type ContentType = 'moment' | 'voice_note' | 'sound_memory';
export type ModerationStatus = 'pending' | 'approved' | 'rejected';

// ─── Database row types ────────────────────────────────────────────────────────

export interface User {
  id: string;
  google_id: string;
  email: string;
  username: string | null;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  created_at: number;
}

export interface Post {
  id: string;
  user_id: string;
  content_type: ContentType;
  moderation_status: ModerationStatus;
  parent_post_id: string | null;
  spotify_track_id: string | null;
  spotify_track_name: string | null;
  spotify_artist: string | null;
  spotify_preview_url: string | null;
  spotify_preview_cached_at: number | null;
  timestamp_start_ms: number | null;
  clip_duration_ms: number | null;
  youtube_video_id: string | null;
  r2_key: string | null;
  duration_ms: number | null;
  waveform: string | null;
  transcript: string | null;
  post_color: PostColorHex | null;
  caption: string | null;
  is_private: number; // 0 | 1 in SQLite
  reply_count: number;
  created_at: number;
}

export interface Resonance {
  user_id: string;
  post_id: string;
  created_at: number;
}

export interface Follow {
  follower_id: string;
  followee_id: string;
  created_at: number;
}

export interface ListeningSession {
  id: string;
  user_id: string;
  completed_at: number;
}

// ─── Request / Response shapes ─────────────────────────────────────────────────

/** Post as it appears in feed/timeline responses */
export interface FeedPost extends Post {
  author_username: string | null;
  author_display_name: string | null;
  author_avatar_url: string | null;
  has_resonated: boolean;
  resonate_count: number;
}

/** Minimal user shape stored in session context */
export interface SessionUser {
  id: string;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
}

/** Public profile response */
export interface PublicProfile {
  id: string;
  username: string | null;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  post_count: number;
  follower_count: number;
  following_count: number;
  is_following: boolean;
}

/** Spotify track search result */
export interface SpotifyTrack {
  trackId: string;
  trackName: string;
  artist: string;
  previewUrl: string | null;
  albumArt: string | null;
  durationMs: number;
}

/** Standard API error envelope */
export interface ApiError {
  error: string;
  status: number;
}

/** Standard API success envelope */
export interface ApiSuccess<T> {
  data: T;
}

/** Cursor pagination metadata */
export interface PaginatedResponse<T> {
  data: T[];
  next_cursor: string | null;
}

/** Journal entry — one per day */
export interface JournalEntry {
  date: string; // 'YYYY-MM-DD'
  content_type: ContentType | null;
}

// ─── Env bindings (Cloudflare Workers) ────────────────────────────────────────

export interface Env {
  DB: D1Database;
  KV: KVNamespace;
  R2: R2Bucket | undefined;
  GOOGLE_CLIENT_ID: string;
  GOOGLE_CLIENT_SECRET: string;
  SPOTIFY_CLIENT_ID: string;
  SPOTIFY_CLIENT_SECRET: string;
  YOUTUBE_API_KEY: string;
  YOUTUBE_API_KEY_2?: string;
  YOUTUBE_API_KEY_3?: string;
  APP_URL: string;
  FRONTEND_URL?: string;
  ASSETS: Fetcher; // Cloudflare Pages static asset binding
}
